import { IDivision } from '@user/store/divisions/typings'
import { ILeadFormModel } from '@user/components/Leads/AddLeadPage/typings'
import { useCallback, useEffect, useMemo } from 'react'
import { useFormikContext, useField } from 'formik'

type Props = {
  name: string
  required?: boolean
  divisions: IDivision[]
}

export function useData(props: Props) {
  const { required, name, divisions } = props

  const options = divisions
  const {
    setFieldValue,
    isSubmitting,
    values: { divisionId, billTo_divisionId },
  } = useFormikContext<ILeadFormModel>()

  const currentValue = useMemo(
    () => (name == 'divisionId' ? divisionId : billTo_divisionId),
    [name, divisionId, billTo_divisionId],
  )

  const [{}, { error, touched }] = useField<string>({
    name,
    validate: (value) => {
      if (required && !value) {
        return 'Required'
      }
    },
  })

  const showError = Boolean(error && touched)
  const helperText = showError ? error : ''

  const getLabel = useCallback(
    (option: IDivision) =>
      option.description.trim() == ''
        ? option.name
        : `${option.name}- ${option.description}`,
    [],
  )

  const getId = useCallback((option: IDivision) => {
    return String(option.id)
  }, [])

  useEffect(() => {
    if (!currentValue && required && options?.length == 1) {
      setFieldValue(name, options[0].id)
    }
  }, [currentValue, required, setFieldValue, options, name])

  const initialOption = useMemo(() => {
    if (currentValue) {
      const value = options.find((x) => x.id === currentValue)
      if (value) return value
    }
    return null
  }, [options, currentValue])

  return {
    getLabel,
    getId,
    items: options,
    initialOption,
    isSubmitting,
    showError,
    helperText,
  }
}
